/**
 * Which face the first-run wizard shows.
 *
 * Pulled out of SetupPage as a plain function because the rule is not obvious
 * and got it wrong once: the three inputs are not independent. On a hosted
 * instance the OIDC callback creates accounts as a side effect of signing
 * people in, so `adminExists` answers "has anyone logged in yet", not "has this
 * box been set up". Only `setup.initialized` answers the second question, and
 * only POST /api/setup/claim sets it.
 *
 * Reading one for the other put the wizard into its standalone half-finished
 * state on a live deployment — offering a reset button, and skipping the claim
 * call so the flag, and with it the whole API gate, stayed off.
 *
 * @param {object} status
 * @param {boolean} status.oidcEnabled  A nyxCore client is configured.
 * @param {boolean} [status.statusKnown=true]  /api/setup/status actually answered.
 * @param {boolean} status.initialized  `setup.initialized` is set.
 * @param {boolean} status.adminExists  At least one local row has role 'admin'.
 * @param {boolean} status.hasToken     The visitor carries a session token.
 * @returns {{stage: string, to?: string}}
 *   `unknown` — could not ask; show nothing actionable.
 *   `redirect` — setup is over; `to` says where.
 *   `claim-signin` — hosted, anonymous: offer the nyxCore sign-in.
 *   `claim` — hosted, signed in: POST the claim, then continue to sources.
 *   `local-admin` — standalone, empty: the create-admin form.
 *   `partial` — standalone, admin exists, nobody signed in: sign in or restart.
 *   `sources` — step 2.
 */
export function resolveSetupStage({
  oidcEnabled = false,
  statusKnown = true,
  initialized = false,
  adminExists = false,
  hasToken = false,
} = {}) {
  if (!statusKnown) return { stage: 'unknown' };

  // Deliberately ahead of every other branch, including the admin-row check: a
  // claimed instance with an empty user table is a broken instance, and
  // re-running setup there would hand it to whoever asked.
  if (initialized) return { stage: 'redirect', to: hasToken ? '/' : '/login' };

  if (oidcEnabled) {
    return hasToken ? { stage: 'claim' } : { stage: 'claim-signin' };
  }

  if (!adminExists) return { stage: 'local-admin' };
  return hasToken ? { stage: 'sources' } : { stage: 'partial' };
}
